<template>
  <!--
    WP-04V final closeout. The outer wrapper exists only so the student header
    can sit OUTSIDE `.session-history`, which carries 53px of page padding that
    would otherwise inset the header. It has no styles of its own, so the
    volunteer layout below is unchanged.

    The header is the SAME `StudentAppHeader` Home and the Tutor use -- no
    page-specific header. `!isVolunteer` is deliberately the same condition
    `App/index.vue` and `AppSidebar/index.vue` use to suppress the legacy
    masthead for this route, so the legacy header is never suppressed without
    this one being rendered.
  -->
  <div
    class="session-history-page"
    :class="{ 'session-history-page--student': !isVolunteer }"
  >
    <student-app-header
      v-if="!isVolunteer"
      :title="$t('appSidebar.sidebarLinks.learningHistory')"
      :ai-tutor-credits-remaining="aiTutorCreditsRemaining"
    />
    <div
      class="session-history"
      :class="{ 'session-history--student': !isVolunteer }"
    >
      <!--
      TASK P1: the VOLUNTEER experience of this shared route/component is
      frozen byte-for-byte inside this branch (unchanged from before P1).
      The redesigned "Learning History" surface only ever renders for a
      non-volunteer (student) user, in the `v-else` branch below.

      Remediation: the fork MUST use the ACTING-CONTEXT getter
      (`user/isVolunteer`, derived from `activeRole`), not the raw
      `user.isVolunteer` field on the user object -- that field reflects
      role MEMBERSHIP (dual-role accounts keep it true even while acting as
      a student) and previously forced the legacy branch on a dual-role
      student. This matches the same acting-context convention already
      used by SidebarLinks/AppSidebar (isMinimalHomeStudent) and
      App/index.vue's showStudentBottomNav.
    -->
      <template v-if="isVolunteer">
        <section class="header">
          <h1 class="title">
            {{ $t('session.sessionHistory.title') }}
          </h1>
          <p v-if="!mobileMode" class="subtitle">
            {{ $t('session.sessionHistory.volunteerSubtitle') }}
          </p>
        </section>
        <div v-if="!mobileMode" class="container">
          <section>
            <div class="spacing--grid spacing--grid-4 session-list__headers">
              <span>{{
                $t('session.sessionHistory.sessionListHeaders.subject')
              }}</span>
              <span>{{
                $t('session.sessionHistory.sessionListHeaders.date')
              }}</span>
              <span>{{
                user.isVolunteer
                  ? $t('session.sessionHistory.sessionListHeaders.student')
                  : $t('session.sessionHistory.sessionListHeaders.coach')
              }}</span>
              <span>{{
                $t('session.sessionHistory.sessionListHeaders.sessionRecap')
              }}</span>
            </div>
            <div v-if="error">
              <h1 class="title title--body">
                {{ error }}
              </h1>
            </div>
            <loader
              v-else-if="isFetchingSessions"
              message="Retrieving your session history"
              class="session-history-loader"
            />
            <div v-else-if="hasNoPastSessions()" class="session-list">
              <h1 class="title title--body">
                {{ $t('session.sessionHistory.titleBody') }}
              </h1>
            </div>
            <ul class="session-list" v-else>
              <li v-for="(session, index) in sessions" :key="session._id">
                <div
                  class="session-list__session session-list__session--grid-4"
                >
                  <div class="session-list__subject-container">
                    <img
                      :src="session.topicIconLink"
                      :alt="`${session.topic} icon`"
                      class="subject-icon"
                    />
                    <div class="subject-name-container">
                      <div class="subject">{{ session.subject }}</div>
                      <span class="subject-time-tutored">
                        {{ getSessionDuration(session.timeTutored) }}
                        {{ $t('session.sessionHistory.minutes') }}</span
                      >
                    </div>
                  </div>
                  <p class="session-list__created-at">
                    {{ getSessionTime(session.createdAt) }}
                  </p>
                  <div class="session-list__partner-name-container">
                    <favoriting-toggle
                      v-if="!user.isVolunteer"
                      :initialIsFavorite="session.isFavorited"
                      :volunteerName="session.volunteerFirstName"
                      :volunteerId="session.volunteerId"
                      v-on:change-favorited="updateFavoritedVolunteers"
                    />
                    <span class="session-list__partner-name">
                      {{
                        user.isVolunteer
                          ? session.studentFirstName
                          : session.volunteerFirstName
                      }}
                    </span>
                  </div>
                  <div class="session-list__session-recap">
                    <large-button
                      primary
                      class="session-list__session-recap__button"
                      @click.native="routeToSessionRecap(session.id)"
                      >{{
                        $t('session.sessionHistory.sessionRecapButton')
                      }}</large-button
                    >
                  </div>
                </div>
                <div class="border--thin" v-if="index !== 5"></div>
              </li>
            </ul>
            <footer class="page-actions-container">
              <div class="page-actions">
                <div
                  @click="() => getSessionHistory(page - 1)"
                  :class="isFirstPage && 'page-actions__stepper--disabled'"
                  class="page-actions__stepper"
                >
                  <caret-icon class="caret caret--previous" /><span>{{
                    $t('general.previous')
                  }}</span>
                </div>
                <div class="page-numbers">
                  <span class="page-num page-num--active">
                    {{ page }}
                  </span>
                </div>
                <div
                  @click="() => getSessionHistory(page + 1)"
                  :class="isLastPage && 'page-actions__stepper--disabled'"
                  class="page-actions__stepper"
                >
                  <span>{{ $t('general.next') }}</span
                  ><caret-icon class="caret caret--next" />
                </div>
              </div>
            </footer>
          </section>
        </div>
        <div v-if="mobileMode">
          <div class="mobile-container">
            <section>
              <div v-if="error">
                <h1 class="title title--body">
                  {{ error }}
                </h1>
              </div>
              <loader
                v-else-if="isFetchingSessions"
                message="Retrieving your session history"
                class="session-history-loader"
              />
              <div v-else-if="hasNoPastSessions()">
                <h1 class="title title--body mobile-session-list">
                  {{ $t('session.sessionHistory.titleBody') }}
                </h1>
              </div>
              <ul v-else class="mobile-session-list">
                <li v-for="(session, index) in sessions" :key="session._id">
                  <div class="mobile-session-list__session">
                    <div class="mobile-session-list__subject-container">
                      <img
                        :src="session.topicIconLink"
                        :alt="`${session.topic} icon`"
                        class="mobile-subject-icon"
                      />
                      <div class="mobile-subject-name-container">
                        <div class="mobile-subject">{{ session.subject }}</div>
                        <span class="mobile-subject-time-tutored">
                          {{ getSessionDuration(session.timeTutored) }}
                          {{ $t('session.sessionHistory.minutes') }}</span
                        >
                      </div>
                    </div>
                    <div class="mobile-session-list__createdAt-container">
                      <div class="mobile-session-list__partner-name-container">
                        <favoriting-toggle
                          v-if="!user.isVolunteer"
                          class="heart"
                          :initialIsFavorite="session.isFavorited"
                          :volunteerName="session.volunteerFirstName"
                          :volunteerId="session.volunteerId"
                          v-on:change-favorited="updateFavoritedVolunteers"
                        />
                        <span class="mobile-session-list__partner-name">
                          {{
                            user.isVolunteer
                              ? session.studentFirstName
                              : session.volunteerFirstName
                          }}
                        </span>
                      </div>
                      <span class="mobile-session-list__created-at">
                        {{ getSessionTimeForMobile(session.createdAt) }}</span
                      >
                    </div>
                    <caret-icon
                      class="caret--next"
                      @click="routeToSessionRecap(session.id)"
                    />
                  </div>
                  <div class="border--thin" v-if="index !== 5"></div>
                </li>
              </ul>
              <footer class="page-actions-container">
                <div class="page-actions">
                  <div
                    @click="() => getSessionHistory(page - 1)"
                    :class="isFirstPage && 'page-actions__stepper--disabled'"
                    class="page-actions__stepper"
                  >
                    <caret-icon class="caret caret--previous" />
                  </div>
                  <div class="page-numbers">
                    <span class="page-num page-num--active">
                      {{ page }}
                    </span>
                  </div>
                  <div
                    @click="() => getSessionHistory(page + 1)"
                    :class="isLastPage && 'page-actions__stepper--disabled'"
                    class="page-actions__stepper"
                  >
                    <caret-icon class="caret caret--next" />
                  </div>
                </div>
              </footer>
            </section>
          </div>
        </div>
      </template>

      <!-- CAT-1C (doc 21 §1.3, DESIGN-18): the Student Reports V2 surface,
         composed to the rendered canonical prototype's Reports screen -- page
         intro, four summary tiles, the weekly-insight slot, the filter row and
         one column of V2 report cards (the active teacher request, the AI
         Tutor conversation catalog from CAT-1A, the human teacher sessions).
         Real CAT-1A/B data only: a genuine 0 renders as 0, an unobserved value
         never does, unsupported surfaces keep their V2 place in an explicit
         unavailable / disabled state (00 §23.3). -->
      <template v-else>
        <section class="learning-history reports" data-testid="reports-v2">
          <header class="learning-history__header">
            <span class="learning-history__eyebrow">{{
              $t('aiTutorSetup.reports.intro.eyebrow')
            }}</span>
            <h1 class="learning-history__title">
              {{ $t('aiTutorSetup.reports.intro.title') }}
            </h1>
            <p class="learning-history__subtitle">
              {{ $t('aiTutorSetup.reports.intro.lead') }}
            </p>
          </header>

          <!-- V405-A (doc 26 §5): the V405 toolbar -- a subject filter and the
               overall summary. Real data only: the options are the courses of
               the student's own conversation catalog, selecting one re-reads
               the catalog through the API's existing `course` parameter, and
               the summary sentence carries only numbers the summary really
               returned. -->
          <section class="reports-toolbar" data-testid="reports-toolbar">
            <div class="reports-toolbar__filter">
              <label for="reports-subject-filter">
                <student-home-icon name="filter" :size="16" />
                {{ $t('aiTutorSetup.reports.toolbar.subjectLabel') }}
              </label>
              <select
                id="reports-subject-filter"
                data-testid="reports-subject-filter"
                :value="courseFilter"
                :aria-label="$t('aiTutorSetup.reports.toolbar.subjectAria')"
                @change="setCourseFilter($event.target.value)"
              >
                <option value="all">{{
                  $t('aiTutorSetup.reports.toolbar.allSubjects')
                }}</option>
                <option
                  v-for="course in aiTutorCourses"
                  :key="course.name"
                  :value="course.name"
                  >{{ course.label }}</option
                >
              </select>
            </div>
            <div
              class="reports-toolbar__summary"
              data-testid="reports-overall-summary"
              :data-status="overallSummary.status"
            >
              <span class="reports-toolbar__summary-icon" aria-hidden="true">
                <student-home-icon name="chart" :size="18" />
              </span>
              <div class="reports-toolbar__summary-copy">
                <strong>{{ $t('aiTutorSetup.reports.overall.title') }}</strong>
                <span>{{ overallSummary.text }}</span>
              </div>
            </div>
          </section>

          <!-- Summary tiles: the V2 four, each in one of the approved
               availability presentations (available / partial / not_observable
               / unsupported / unavailable / loading). No number ever appears
               for a status that has none. -->
          <section
            class="reports-metrics"
            data-testid="ai-tutor-reports-summary"
            :aria-busy="summaryLoading ? 'true' : 'false'"
          >
            <article
              v-for="tile in metricTiles"
              :key="tile.key"
              class="reports-tile"
              :class="'reports-tile--' + tile.status"
              :data-testid="'ai-tutor-reports-metric-' + tile.key"
              :data-status="tile.status"
              :aria-disabled="tile.hasNumber ? null : 'true'"
            >
              <span class="reports-tile__icon" aria-hidden="true">
                <student-home-icon :name="tile.icon" :size="24" />
              </span>
              <strong class="reports-tile__value">{{ tile.value }}</strong>
              <span class="reports-tile__label"
                >{{ tile.label
                }}<small v-if="tile.note" class="reports-tile__note">{{
                  tile.note
                }}</small></span
              >
            </article>
          </section>

          <!-- Weekly insight: V2's slot, kept in its place, in the approved
               unavailable state -- no generated prose, no suggested practice. -->
          <section
            class="reports-insight reports-insight--unavailable"
            data-testid="reports-insight"
            aria-disabled="true"
          >
            <div class="reports-insight__body">
              <span class="reports-insight__icon" aria-hidden="true">
                <student-home-icon name="spark" :size="22" />
              </span>
              <div class="reports-insight__copy">
                <strong class="reports-insight__title">{{
                  $t('aiTutorSetup.reports.insight.title')
                }}</strong>
                <p class="reports-insight__text">
                  {{ $t('aiTutorSetup.reports.insight.unavailable') }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="reports-insight__action"
              disabled
              data-testid="reports-insight-action"
            >
              {{ $t('aiTutorSetup.reports.insight.action') }}
            </button>
          </section>

          <!-- Filters: only «همه کارت‌ها» and «با معلم برنا» have real
               semantics today; the learning-state filters keep their V2 place
               but are disabled and non-actionable (no fake client filtering). -->
          <div
            class="reports-filters"
            role="group"
            :aria-label="$t('aiTutorSetup.reports.filters.label')"
          >
            <button
              v-for="option in reportFilters"
              :key="option.key"
              type="button"
              class="reports-filter"
              :class="{
                'reports-filter--active': option.key === reportFilter,
                'reports-filter--disabled': !option.enabled,
              }"
              :disabled="!option.enabled"
              :aria-pressed="
                option.enabled ? String(option.key === reportFilter) : null
              "
              :aria-disabled="option.enabled ? null : 'true'"
              :aria-describedby="
                option.enabled ? null : 'reports-filter-unavailable'
              "
              :title="
                option.enabled
                  ? null
                  : $t('aiTutorSetup.reports.filters.unsupported')
              "
              :data-testid="'reports-filter-' + option.key"
              @click="setReportFilter(option)"
            >
              {{ option.label }}
            </button>
            <!-- P42-10: the visible «غیرفعال» marker is gone; the dashed
                 look stays and a screen reader still hears why the filter
                 cannot be chosen. -->
            <span
              id="reports-filter-unavailable"
              class="reports-visually-hidden"
              >{{ $t('aiTutorSetup.reports.filters.unsupported') }}</span
            >
          </div>

          <div class="reports-list" data-testid="reports-list">
            <!-- P1: the student's real active/pending teacher request, backed by
                 the existing session state and getters; V2's «در انتظار معلم»
                 card. No request code, deadline or teacher count is invented. -->
            <article
              v-if="
                showTeacherSources &&
                  (isSessionWaitingForVolunteer || isSessionInProgress)
              "
              class="reports-card reports-card--request"
              role="button"
              tabindex="0"
              data-testid="reports-active-request"
              @click="rejoinActiveSession"
              @keyup.enter="rejoinActiveSession"
            >
              <div class="reports-card__head">
                <span class="reports-pill reports-pill--warning">
                  <span class="reports-pill__dot" aria-hidden="true" />
                  {{
                    isSessionInProgress
                      ? $t(
                          'session.sessionHistory.learningHistory.activeRequest.inProgress'
                        )
                      : $t(
                          'session.sessionHistory.learningHistory.activeRequest.waiting'
                        )
                  }}
                </span>
              </div>
              <h3 class="reports-card__title reports-card__title--request">
                {{ $t('rejoinSessionHeader.message') }}
              </h3>
              <div class="reports-card__foot">
                <span class="reports-card__note" />
                <strong class="reports-card__action">
                  {{ $t('aiTutorSetup.reports.card.rejoin') }}
                  <student-home-icon name="arrow" :size="17" />
                </strong>
              </div>
            </article>

            <!-- Both sources empty: one honest empty card, not two. -->
            <div
              v-if="showCombinedEmpty"
              class="reports-state learning-history__state"
              data-testid="reports-empty"
            >
              <archive-icon class="learning-history__state-icon" />
              <h2 class="title title--body">
                {{ $t('aiTutorSetup.reports.states.allEmpty') }}
              </h2>
            </div>

            <!-- CAT-1A (doc 20 §13): the AI Tutor conversation catalog -- Borna's
                 own cross-course record, read from the pure-DB Reports API
                 (never Synapse); survives a reload, needs no course context. -->
            <section
              v-if="showAiTutorHistory && reportFilter !== 'teacher'"
              class="reports-group"
              data-testid="ai-tutor-history"
              :data-status-filter="activeStatusFilter || 'all'"
              :data-total="aiTutorTotal === null ? null : String(aiTutorTotal)"
              :data-page="String(aiTutorPage)"
              :data-loading="aiTutorLoadKind || null"
              :aria-busy="aiTutorLoadKind ? 'true' : null"
            >
              <!-- P41 QA-39: the first load shows the shape of the cards to
                   come (the approved report card, its parts as grey bars),
                   never an empty section. -->
              <div
                v-if="aiTutorLoadKind === 'initial'"
                class="reports-skeletons"
                role="status"
                data-testid="ai-tutor-history-loading"
              >
                <span class="reports-visually-hidden">{{
                  $t('aiTutorSetup.reports.loading')
                }}</span>
                <div
                  v-for="n in 3"
                  :key="'skeleton-' + n"
                  class="reports-skeleton-card"
                  aria-hidden="true"
                  data-testid="ai-tutor-history-skeleton"
                >
                  <div class="reports-card__head">
                    <span class="reports-skeleton reports-skeleton--pill" />
                    <span class="reports-skeleton reports-skeleton--time" />
                  </div>
                  <div class="reports-card__source">
                    <span
                      class="reports-card__icon reports-skeleton reports-skeleton--icon"
                    />
                    <div class="reports-card__copy">
                      <span class="reports-skeleton reports-skeleton--title" />
                      <span class="reports-skeleton reports-skeleton--meta" />
                    </div>
                  </div>
                  <div class="reports-card__foot">
                    <span class="reports-skeleton reports-skeleton--note" />
                    <span class="reports-skeleton reports-skeleton--action" />
                  </div>
                </div>
              </div>
              <!-- P41 QA-39: a filter or page change -- the cards on screen
                   belong to the previous choice, so they are dimmed and inert
                   until the new list replaces them (no flicker, no jump). -->
              <p
                v-if="aiTutorLoadKind === 'refresh'"
                class="reports-refreshing"
                role="status"
                data-testid="ai-tutor-history-refreshing"
              >
                <span class="reports-refreshing__bar" aria-hidden="true" />
                {{ $t('aiTutorSetup.reports.loading') }}
              </p>
              <div
                v-else-if="aiTutorStatus === 'error'"
                class="reports-state learning-history__ai-state"
                role="status"
                data-testid="ai-tutor-history-error"
              >
                <h2 class="title title--body">
                  {{ $t('aiTutorSetup.reports.loadError') }}
                </h2>
                <button
                  type="button"
                  class="reports-state__retry"
                  data-testid="ai-tutor-history-retry"
                  @click="loadAiTutorHistory"
                >
                  {{ $t('aiTutorSetup.reports.retry') }}
                </button>
              </div>
              <div
                v-else-if="
                  aiTutorStatus === 'loaded' &&
                    !aiTutorConversations.length &&
                    !showCombinedEmpty
                "
                class="reports-state learning-history__ai-state"
                data-testid="ai-tutor-history-empty"
              >
                <h2 class="title title--body">
                  {{ $t('aiTutorSetup.reports.empty') }}
                </h2>
              </div>

              <p
                v-if="aiTutorOpenFailed"
                class="reports-note"
                role="alert"
                data-testid="ai-tutor-history-open-error"
              >
                {{ $t('aiTutorSetup.chat.actionErrorNote') }}
              </p>
              <p
                v-if="aiTutorBoardOpenFailed"
                class="reports-note"
                role="alert"
                data-testid="ai-tutor-history-board-open-error"
              >
                {{ $t('aiTutorSetup.chat.errors.network') }}
              </p>

              <!-- Exactly the rows the API returned, in its order (newest
                   first). Zero-turn / unknown-turn rows are hidden by the
                   backend (H-6); nothing is added or re-sorted here. -->
              <article
                v-for="conversation in aiTutorConversations"
                :key="conversation.id"
                class="reports-card"
                :class="{
                  'reports-card--stale': aiTutorLoadKind === 'refresh',
                }"
                role="button"
                :tabindex="aiTutorLoadKind === 'refresh' ? -1 : 0"
                :aria-disabled="aiTutorLoadKind === 'refresh' ? 'true' : null"
                :aria-current="
                  isActiveAiTutorConversation(conversation) ? 'true' : null
                "
                :data-testid="
                  'ai-tutor-history-session-' + conversation.synapseSessionId
                "
                :data-conversation-id="conversation.id"
                @click="resumeAiTutorConversation(conversation)"
                @keyup.enter="resumeAiTutorConversation(conversation)"
              >
                <div class="reports-card__head">
                  <span
                    class="reports-pill"
                    :class="
                      'reports-pill--' +
                        aiTutorConversationStatus(conversation).tone
                    "
                    :data-kind="aiTutorConversationStatus(conversation).kind"
                    data-testid="ai-tutor-history-status"
                  >
                    <span
                      v-if="
                        aiTutorConversationStatus(conversation).icon === 'dot'
                      "
                      class="reports-pill__dot"
                      aria-hidden="true"
                    />
                    <student-home-icon
                      v-else
                      :name="aiTutorConversationStatus(conversation).icon"
                      :size="14"
                    />
                    {{ aiTutorConversationStatus(conversation).label }}
                  </span>
                  <time
                    class="reports-card__time"
                    :datetime="
                      conversation.lastTurnAt || conversation.createdAt
                    "
                    >{{ aiTutorConversationDate(conversation) }}</time
                  >
                </div>

                <div class="reports-card__source">
                  <span
                    class="reports-card__icon"
                    :class="
                      'reports-card__icon--' +
                        aiTutorConversationIcon(conversation).tone
                    "
                    :data-intent-icon="
                      aiTutorConversationIcon(conversation).name
                    "
                    aria-hidden="true"
                  >
                    <student-home-icon
                      :name="aiTutorConversationIcon(conversation).name"
                      :size="22"
                    />
                  </span>
                  <div class="reports-card__copy">
                    <!-- P40 QA-33: a title that carries $…$ math (a learn
                         topic or the tutor's current node) is typeset by the
                         chat's own safe renderer; every other title stays
                         the plain text node it was. -->
                    <h3 class="reports-card__title">
                      <markdown-content
                        v-if="aiTutorTitleHasMath(conversation)"
                        inline
                        :source="aiTutorConversationTitle(conversation)"
                      />
                      <template v-else>
                        {{ aiTutorConversationTitle(conversation) }}
                      </template>
                    </h3>
                    <p class="reports-card__meta">
                      {{ aiTutorConversationMeta(conversation) }}
                    </p>
                  </div>
                </div>

                <div class="reports-card__foot">
                  <span class="reports-card__note">{{
                    aiTutorConversationNote(conversation)
                  }}</span>
                  <!-- WB-M1A (doc 23 §13.4.1): the one entry to this
                       conversation's board. Offered only when Subway's
                       server-driven capability says the whiteboard is
                       available and the conversation is active. A `v-for`
                       over a list that is EMPTY otherwise, rather than a
                       `v-if`: an empty list leaves nothing behind, so
                       without the capability this card's DOM is exactly
                       what it was. Its click must not also resume the
                       chat. -->
                  <button
                    v-for="entry in aiTutorBoardEntries(conversation)"
                    :key="entry"
                    type="button"
                    class="reports-board-entry"
                    data-testid="ai-tutor-history-board"
                    :tabindex="aiTutorLoadKind === 'refresh' ? -1 : null"
                    :aria-label="$t('aiTutorBoard.entryLabel')"
                    :title="$t('aiTutorBoard.entryLabel')"
                    @click.stop="openAiTutorBoard(conversation)"
                    @keyup.enter.stop
                  >
                    <!-- P42-12: the board icon replaces the «تخته» text; the
                         accessible name and the destination are unchanged. -->
                    <student-home-icon name="board" :size="18" />
                  </button>
                  <strong class="reports-card__action">
                    {{
                      isActiveAiTutorConversation(conversation)
                        ? $t('aiTutorSetup.reports.status.current')
                        : $t('aiTutorSetup.reports.card.resume')
                    }}
                    <student-home-icon name="arrow" :size="17" />
                  </strong>
                </div>
              </article>

              <!-- P41 QA-34: the catalog is paged by the Reports API itself
                   (`page` / `limit` / `total`); the same pager as the teacher
                   sessions below. -->
              <nav
                v-if="aiTutorPageCount > 1 && aiTutorStatus !== 'error'"
                class="reports-pager"
                :aria-label="$t('aiTutorSetup.reports.aiPager.label')"
                data-testid="ai-tutor-history-pager"
              >
                <button
                  type="button"
                  class="reports-pager__step"
                  :disabled="aiTutorPage <= 1 || !!aiTutorLoadKind"
                  data-testid="ai-tutor-history-pager-previous"
                  @click="goToAiTutorPage(aiTutorPage - 1)"
                >
                  <student-home-icon name="back" :size="16" />
                  {{ $t('aiTutorSetup.reports.pager.previous') }}
                </button>
                <span
                  class="reports-pager__page"
                  aria-current="page"
                  data-testid="ai-tutor-history-pager-page"
                  >{{
                    $t('aiTutorSetup.reports.aiPager.page', {
                      page: formatCount(aiTutorPage),
                      pages: formatCount(aiTutorPageCount),
                    })
                  }}</span
                >
                <button
                  type="button"
                  class="reports-pager__step"
                  :disabled="
                    aiTutorPage >= aiTutorPageCount || !!aiTutorLoadKind
                  "
                  data-testid="ai-tutor-history-pager-next"
                  @click="goToAiTutorPage(aiTutorPage + 1)"
                >
                  {{ $t('aiTutorSetup.reports.pager.next') }}
                  <student-home-icon name="arrow" :size="16" />
                </button>
              </nav>
            </section>

            <!-- P1: the human teacher sessions (`/api/sessions/history`), as V2
                 report cards; recap, favoriting and pagination are the existing
                 real behaviors. V405-A: they carry no course of the education
                 catalog, so they are listed only under «همه درس‌ها». -->
            <section
              v-if="showTeacherSources"
              class="reports-group"
              data-testid="teacher-sessions"
            >
              <div v-if="error" class="reports-state learning-history__state">
                <h2 class="title title--body">{{ error }}</h2>
              </div>
              <!-- P41 QA-39: loading until BOTH the page and the total are in;
                   the total alone decides "empty", so it never flashes. -->
              <loader
                v-else-if="isFetchingSessions || !teacherTotalLoaded"
                :message="$t('aiTutorSetup.reports.states.teacherLoading')"
                class="session-history-loader"
              />
              <div
                v-else-if="hasNoPastSessions() && !showCombinedEmpty"
                class="reports-state learning-history__state"
                data-testid="teacher-sessions-empty"
              >
                <archive-icon class="learning-history__state-icon" />
                <h2 class="title title--body">
                  {{ $t('aiTutorSetup.reports.states.teacherEmpty') }}
                </h2>
              </div>

              <template v-else-if="!hasNoPastSessions()">
                <article
                  v-for="session in sessions"
                  :key="session._id"
                  class="reports-card reports-card--teacher"
                  role="button"
                  tabindex="0"
                  :data-testid="'teacher-session-' + session.id"
                  @click="routeToSessionRecap(session.id)"
                  @keyup.enter="routeToSessionRecap(session.id)"
                >
                  <div class="reports-card__head">
                    <span class="reports-pill reports-pill--teal">
                      <student-home-icon name="teacher" :size="14" />
                      {{ $t('aiTutorSetup.reports.status.teacherSession') }}
                    </span>
                    <time
                      class="reports-card__time"
                      :datetime="session.createdAt"
                      >{{ getSessionTime(session.createdAt) }}</time
                    >
                  </div>

                  <div class="reports-card__source">
                    <span
                      class="reports-card__icon reports-card__icon--teal"
                      aria-hidden="true"
                    >
                      <student-home-icon name="teacher" :size="22" />
                    </span>
                    <div class="reports-card__copy">
                      <h3 class="reports-card__title">
                        {{ session.subject }}
                      </h3>
                      <p class="reports-card__meta">
                        {{ teacherSessionMeta(session) }}
                      </p>
                    </div>
                  </div>

                  <div class="reports-card__foot">
                    <!-- The favorite toggle is its own control; its click must
                         not also open the recap. -->
                    <span
                      class="reports-card__note reports-card__partner"
                      @click.stop
                      @keyup.enter.stop
                    >
                      <favoriting-toggle
                        :initialIsFavorite="session.isFavorited"
                        :volunteerName="session.volunteerFirstName"
                        :volunteerId="session.volunteerId"
                        v-on:change-favorited="updateFavoritedVolunteers"
                      />
                      <span class="reports-card__partner-name">{{
                        session.volunteerFirstName
                      }}</span>
                    </span>
                    <strong class="reports-card__action">
                      {{ $t('aiTutorSetup.reports.card.recap') }}
                      <student-home-icon name="arrow" :size="17" />
                    </strong>
                  </div>
                </article>

                <nav
                  v-if="totalPages > 1"
                  class="reports-pager"
                  :aria-label="$t('aiTutorSetup.reports.pager.label')"
                  data-testid="reports-pager"
                >
                  <button
                    type="button"
                    class="reports-pager__step"
                    :disabled="isFirstPage"
                    data-testid="reports-pager-previous"
                    @click="getSessionHistory(page - 1)"
                  >
                    <student-home-icon name="back" :size="16" />
                    {{ $t('aiTutorSetup.reports.pager.previous') }}
                  </button>
                  <span class="reports-pager__page" aria-current="page">{{
                    $t('aiTutorSetup.reports.pager.page', {
                      page: formatCount(page),
                    })
                  }}</span>
                  <button
                    type="button"
                    class="reports-pager__step"
                    :disabled="isLastPage"
                    data-testid="reports-pager-next"
                    @click="getSessionHistory(page + 1)"
                  >
                    {{ $t('aiTutorSetup.reports.pager.next') }}
                    <student-home-icon name="arrow" :size="16" />
                  </button>
                </nav>
              </template>
            </section>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import NetworkService from '../services/NetworkService'
import CaretIcon from '@/assets/caret.svg'
import ArchiveIcon from '@/assets/archive.svg'
import FavoritingToggle from '../components/FavoritingToggle.vue'
import LargeButton from '../components/LargeButton.vue'
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import StudentAppHeader from '@/components/StudentShell/StudentAppHeader.vue'
import MarkdownContent from '@/components/AiTutor/Chat/MarkdownContent.vue'
import { mapState, mapGetters } from 'vuex'
import { isNavigationFailure } from 'vue-router'
import moment from 'moment'
import Loader from '@/components/Loader.vue'
import { backOff } from 'exponential-backoff'
import LoggerService from '@/services/LoggerService'
import AnalyticsService from '@/services/AnalyticsService'
import { EVENTS } from '@/consts'
import i18n from '@/i18n'
import * as sessionUtils from '@/utils/session'
import { formatReportCount, formatReportTime } from '@/utils/reportsFormat'
// CAT-1A: the Reports API is reached through the aiTutor store's
// collaborator seam so unit tests can double it the same way they double
// the chat's API (no jest.mock in this repo).
import { collaborators as aiTutorCollaborators } from '@/store/modules/aiTutor'

// Batch 45 (doc 45, Owner decision, supersedes P41's 50): conversations per page.
const CONVERSATIONS_PAGE_LIMIT = 10
// The subject filter offers the courses of the newest conversations, more than
// one page shows. The Reports API's own maximum.
const COURSE_OPTIONS_LIMIT = 50
// P42-06: a return to the page re-reads at most this often (a tab switched
// back and forth is not a request storm).
const RETURN_REFRESH_MIN_GAP_MS = 1500
const INTENT_LABEL_KEYS = {
  troubleshoot: 'aiTutorSetup.intentStep.options.troubleshoot.pillLabel',
  review: 'aiTutorSetup.intentStep.options.review.pillLabel',
  learn: 'aiTutorSetup.intentStep.options.learn.pillLabel',
}
// CAT-1C: the V2 intent-icon treatment per setup mode (the reference's
// `.intent-icon--indigo/--coral` tiles and their glyphs). A backfilled row
// without an intent gets the neutral spark.
// P42-07: `review` has its own `check` glyph (as in Setup and Home) so a
// "check my answer" card no longer looks like a card without an intent.
const INTENT_ICONS = {
  troubleshoot: { name: 'chat', tone: 'indigo' },
  review: { name: 'check', tone: 'indigo' },
  learn: { name: 'book', tone: 'coral' },
}
const NEUTRAL_INTENT_ICON = { name: 'spark', tone: 'indigo' }
// The V2 summary tiles, in the reference's order, mapped onto the CAT-1A
// summary metrics (doc 07 §12.2).
const METRIC_TILES = [
  { key: 'conversations', metric: 'conversationCount', icon: 'chat' },
  { key: 'teacherSessions', metric: 'teacherSessionCount', icon: 'teacher' },
  { key: 'learnedTopics', metric: 'learnedTopicCount', icon: 'check' },
  { key: 'activeDays', metric: 'activeDaysThisWeek', icon: 'spark' },
]
// STUDENT-UX-02 / UX2-03 (doc 30 §5, D-4 … D-8; named-scope supersession of
// doc 20 §15 / doc 21 CAT-1C): the chip row is the report-status taxonomy.
// `status` is the server's bucket code (`GET /ai-tutor/conversations?status=`,
// one precedence shared with the card pill: awaiting > handoff > understood >
// confused > noFeedback); the labels are presentation only (D-6). A chip is
// `enabled` only when a REAL persisted state backs it: «حل با معلم» has none
// yet (D-5) and stays visible but disabled. `teacher` is the legacy human
// teacher-session filter, shown only while the teacher capability is ON (D-7).
const REPORT_FILTERS = [
  { key: 'all', enabled: true },
  { key: 'understood', status: 'understood', enabled: true },
  { key: 'confused', status: 'confused', enabled: true },
  { key: 'awaiting', status: 'awaiting', enabled: true },
  { key: 'handoff', status: 'handoff', enabled: true },
  { key: 'teacher', enabled: true, legacyTeacher: true },
  { key: 'noFeedback', status: 'noFeedback', enabled: true },
  { key: 'teacherResolved', status: 'teacherResolved', enabled: false },
]

// UX2-03: the ONE bucket function behind the card pill and the chip row, the
// same precedence the server applies. `null` = no bucket (a backfill row
// whose feedback was never observed; listed only under «همه»).
function conversationStatusBucket(conversation) {
  if (!conversation) return null
  if (conversation.hasPendingAsk === true) return 'awaiting'
  if (conversation.handoffTrigger) return 'handoff'
  if (conversation.lastFeedbackKind === 'understood') return 'understood'
  if (conversation.lastFeedbackKind === 'confused') return 'confused'
  if (
    conversation.lastFeedbackKind === null &&
    conversation.source === 'student_setup'
  ) {
    return 'noFeedback'
  }
  return null
}

export default {
  name: 'session-history-view',
  components: {
    CaretIcon,
    ArchiveIcon,
    FavoritingToggle,
    LargeButton,
    Loader,
    StudentHomeIcon,
    StudentAppHeader,
    MarkdownContent,
  },
  data() {
    return {
      sessions: [],
      page: 1,
      hasNext: false,
      total: 0,
      isFetchingSessions: false,
      isLastPage: false,
      error: '',
      // AI Tutor history: the conversation being opened, and a failed open.
      aiTutorOpeningId: '',
      aiTutorOpenFailed: false,
      aiTutorBoardOpenFailed: false,
      // CAT-1A: the Borna conversation catalog (cross-course) and the Reports
      // summary, read from the pure-DB Reports API.
      aiTutorConversations: [],
      aiTutorStatus: 'idle',
      aiTutorSummary: null,
      aiTutorSummaryStatus: 'idle',
      // CAT-1C: the active V2 filter pill ('all' | 'teacher').
      reportFilter: 'all',
      // UX2-03: the server's `total` for the current chip/course read.
      aiTutorTotal: null,
      // P41 QA-34: the page of the catalog on screen, and the page size the
      // server answered with (it caps the requested limit).
      aiTutorPage: 1,
      aiTutorPageLimit: CONVERSATIONS_PAGE_LIMIT,
      // P41 QA-39: the teacher total has been read (until then "no teacher
      // sessions" is not known, so it is not shown).
      teacherTotalLoaded: false,
      // V405-A: the subject filter -- 'all' or an `education_courses.name` --
      // and its options, taken from the UNFILTERED catalog read so that
      // choosing a course never removes the other choices.
      courseFilter: 'all',
      aiTutorCourses: [],
    }
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
    }),
    ...mapGetters({
      mobileMode: 'app/mobileMode',
      // Acting-context role (activeRole === 'volunteer'), NOT the raw
      // user.isVolunteer field -- see the template comment above.
      isVolunteer: 'user/isVolunteer',
      // UX2-03 (D-7): the SX-02 teacher capability decides whether the legacy
      // «با معلم برنا» chip is offered; a missing getter reads as OFF.
      teacherExperienceAvailable:
        'featureFlags/isStudentTeacherExperienceAvailable',
      // P1: the student's real active/pending request, backed by the same
      // session state + getters the existing RejoinSessionHeader banner
      // uses. No new endpoint or local "authoritative" state is invented.
      isSessionWaitingForVolunteer: 'user/isSessionWaitingForVolunteer',
      isSessionInProgress: 'user/isSessionInProgress',
      sessionPath: 'user/sessionPath',
    }),
    // CR-03 (doc 28 §16/§17): Subway's Student AI Tutor credit balance for the
    // header pill; null (unknown / not a Student) hides it.
    aiTutorCreditsRemaining() {
      const credits = this.$store.getters['user/aiTutorCreditsRemaining']
      return typeof credits === 'number' ? credits : null
    },
    isFirstPage() {
      return this.page === 1
    },
    // CAT-1A: the AI Tutor section is Borna's own catalog, so it is shown for
    // every acting student regardless of any course context in the store
    // (the section used to exist only while the chat's course-scoped list was
    // cached). The module may not be registered at all in a bare test store,
    // hence the defensive reads for the active-session marker.
    aiTutorStore() {
      return this.$store.state.aiTutor || null
    },
    showAiTutorHistory() {
      return !this.isVolunteer
    },
    summaryLoading() {
      return this.aiTutorSummaryStatus === 'loading'
    },
    // Whether Borna's feedback ledger backs `lastFeedbackKind` (CAT-1B). Only
    // then is "no feedback recorded" a statement about the data rather than
    // about what Borna cannot see.
    feedbackObservable() {
      const capabilities =
        this.aiTutorSummary && this.aiTutorSummary.capabilities
      return Boolean(
        capabilities && capabilities.lastFeedbackKind === 'available'
      )
    },
    // WB-M1A: Subway's server-driven whiteboard capability, from the same
    // summary block. Anything but an explicit 'available' (an older Subway
    // that does not report it, a failed summary load) offers no entry; the
    // board routes enforce the same value themselves.
    whiteboardAvailable() {
      const capabilities =
        this.aiTutorSummary && this.aiTutorSummary.capabilities
      return Boolean(capabilities && capabilities.whiteboard === 'available')
    },
    // CAT-1C: the four V2 summary tiles. Every tile is always present; its
    // `status` picks the approved presentation and only `available` /
    // `partial` carry a number (H-5: unknown is never 0).
    metricTiles() {
      const metrics = this.aiTutorSummary && this.aiTutorSummary.metrics
      return METRIC_TILES.map(tile => {
        const base = {
          key: tile.key,
          icon: tile.icon,
          label: this.$t(`aiTutorSetup.reports.tiles.${tile.key}`),
          hasNumber: false,
        }
        if (this.summaryLoading) {
          return {
            ...base,
            status: 'loading',
            value: this.$t('aiTutorSetup.reports.tiles.noValue'),
            note: this.$t('aiTutorSetup.reports.tiles.loading'),
          }
        }
        const metric = metrics && metrics[tile.metric]
        if (!metric) {
          return {
            ...base,
            status: 'unavailable',
            value: this.$t('aiTutorSetup.reports.tiles.noValue'),
            note: this.$t('aiTutorSetup.reports.tiles.unavailable'),
          }
        }
        const numeric = typeof metric.value === 'number'
        if (metric.status === 'available' && numeric) {
          return {
            ...base,
            status: 'available',
            hasNumber: true,
            value: this.formatCount(metric.value),
            note: '',
          }
        }
        if (metric.status === 'partial' && numeric) {
          return {
            ...base,
            status: 'partial',
            hasNumber: true,
            value: this.formatCount(metric.value),
            note: this.$t('aiTutorSetup.reports.tiles.partial'),
          }
        }
        if (metric.status === 'not_observable') {
          return {
            ...base,
            status: 'not_observable',
            value: this.$t('aiTutorSetup.reports.tiles.noValue'),
            note: this.$t('aiTutorSetup.reports.tiles.notObservable'),
          }
        }
        return {
          ...base,
          status: 'unsupported',
          value: this.$t('aiTutorSetup.reports.tiles.noValue'),
          note: this.$t('aiTutorSetup.reports.tiles.unsupported'),
        }
      })
    },
    reportFilters() {
      return REPORT_FILTERS.filter(
        option => !option.legacyTeacher || this.teacherExperienceAvailable
      ).map(option => ({
        ...option,
        label: this.$t(`aiTutorSetup.reports.filters.${option.key}`),
      }))
    },
    // UX2-03: the server bucket of the chosen chip, or null for «همه».
    activeStatusFilter() {
      const option = REPORT_FILTERS.find(o => o.key === this.reportFilter)
      return (option && option.status) || null
    },
    // V405-A: teacher sessions and the active request have no course, so a
    // chosen course shows the AI Tutor conversations of that course only.
    showTeacherSources() {
      return this.courseFilter === 'all'
    },
    // V405-A: «گزارش کلی یادگیری». Only metrics that really carry a number
    // (`available` / `partial`) are named; with none, the sentence says so.
    overallSummary() {
      const t = (key, values) =>
        this.$t(`aiTutorSetup.reports.overall.${key}`, values)
      if (this.summaryLoading) return { status: 'loading', text: t('loading') }
      const metrics = (this.aiTutorSummary && this.aiTutorSummary.metrics) || {}
      const count = name => {
        const metric = metrics[name]
        const observed =
          metric &&
          (metric.status === 'available' || metric.status === 'partial') &&
          typeof metric.value === 'number'
        return observed ? this.formatCount(metric.value) : null
      }
      const conversations = count('conversationCount')
      const teacherSessions = count('teacherSessionCount')
      if (conversations !== null && teacherSessions !== null) {
        return {
          status: 'available',
          text: t('both', { conversations, teacherSessions }),
        }
      }
      if (conversations !== null) {
        return {
          status: 'available',
          text: t('conversationsOnly', { conversations }),
        }
      }
      if (teacherSessions !== null) {
        return {
          status: 'available',
          text: t('teacherSessionsOnly', { teacherSessions }),
        }
      }
      return { status: 'unavailable', text: t('unavailable') }
    },
    // Both sources loaded and empty -> one empty card for the whole list.
    showCombinedEmpty() {
      return (
        this.courseFilter === 'all' &&
        this.reportFilter === 'all' &&
        this.aiTutorStatus === 'loaded' &&
        this.aiTutorConversations.length === 0 &&
        !this.isFetchingSessions &&
        this.teacherTotalLoaded &&
        !this.error &&
        this.total === 0
      )
    },
    // P41 QA-39: which wait the AI Tutor section is in -- 'initial' (nothing
    // to show yet: skeleton cards), 'refresh' (the cards on screen belong to
    // the previous filter or page: dimmed and inert) or '' (not loading).
    aiTutorLoadKind() {
      if (this.aiTutorStatus !== 'loading') return ''
      return this.aiTutorConversations.length ? 'refresh' : 'initial'
    },
    // P41 QA-34: the number of pages the server's `total` makes.
    aiTutorPageCount() {
      const total = this.aiTutorTotal
      const limit = this.aiTutorPageLimit
      if (typeof total !== 'number' || total <= 0 || !limit) return 1
      return Math.max(1, Math.ceil(total / limit))
    },
    totalPages() {
      const sessionLimitPerPage = 5
      const totalPages = Math.ceil(this.total / sessionLimitPerPage)
      return totalPages === 0 ? 1 : totalPages
    },
    // P42-06: whether a Reports read (the list or the summary) is running.
    aiTutorReading() {
      return (
        this.aiTutorStatus === 'loading' ||
        this.aiTutorSummaryStatus === 'loading'
      )
    },
  },
  watch: {
    // P42-06: Subway CONFIRMED a «فهمیدم / نفهمیدم» tap (the store counts only
    // a 2xx). A read that began while that request was still in transit may
    // predate it, so it is read once more. Never on a failed tap.
    'aiTutorStore.feedbackRecordedCount'() {
      this.refreshAiTutorFreshness('feedback')
    },
    // A read that was asked for while another was running waits for it, then
    // runs once (see `refreshAiTutorFreshness`).
    aiTutorReading(reading) {
      if (!reading && this.aiTutorRefreshQueued) {
        this.aiTutorRefreshQueued = false
        this.refreshAiTutorFreshness('queued')
      }
    },
  },
  methods: {
    async getSessionHistory(page) {
      if (page < 1 || page > this.totalPages) return
      this.isFetchingSessions = true
      if (page > this.page)
        AnalyticsService.captureEvent(
          EVENTS.VOLUNTEER_CLICKED_NEXT_SESSION_HISTORY_PAGE,
          {
            page,
          }
        )
      if (page < this.page)
        AnalyticsService.captureEvent(
          EVENTS.VOLUNTEER_CLICKED_PREVIOUS_SESSION_HISTORY_PAGE,
          {
            page,
          }
        )
      try {
        const response = await backOff(() =>
          NetworkService.getSessionHistory(page)
        )
        this.sessions = response.data.pastSessions
        this.isLastPage = response.data.isLastPage
        this.page = page
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isFetchingSessions = false
      }
    },
    async getTotalSessions() {
      try {
        const response = await backOff(() =>
          NetworkService.getTotalSessionHistory()
        )
        this.total = response.data.total
      } catch (error) {
        this.handleError(error)
      } finally {
        this.teacherTotalLoaded = true
      }
    },
    handleError(error) {
      LoggerService.noticeError(error.response.data.err)
      this.error = this.$root.$t('session.sessionHistory.errorContext')
    },
    // The legacy (volunteer) date format is untouched; the student cards use
    // the V2 timestamp below.
    getSessionTime(sessionCreatedAt) {
      if (!this.isVolunteer) return this.formatReportTime(sessionCreatedAt)
      return moment(sessionCreatedAt).format('l @ h:mm A')
    },
    getSessionTimeForMobile(sessionCreatedAt) {
      return moment(sessionCreatedAt).format('l h:mm A')
    },
    getSessionDuration(timeTutored) {
      const duration = Math.ceil(timeTutored / (1000 * 60))
      return duration
    },
    hasNoPastSessions() {
      return this.total === 0
    },
    updateFavoritedVolunteers(volunteerId, isFavorited) {
      this.sessions = this.sessions.map(session => ({
        ...session,
        isFavorited:
          session.volunteerId === volunteerId
            ? isFavorited
            : session.isFavorited,
      }))
    },
    routeToSessionRecap(sessionId) {
      this.$router.push(`/${i18n.locale}/sessions/${sessionId}/recap`)
    },
    rejoinActiveSession() {
      sessionUtils.rejoinSession(this.$router, this.sessionPath)
    },
    // CAT-1C presentation helpers (pure formatting; see utils/reportsFormat).
    formatCount(value) {
      return formatReportCount(value, i18n.locale)
    },
    formatReportTime(iso) {
      return formatReportTime(iso, {
        locale: i18n.locale,
        today: this.$t('aiTutorSetup.reports.time.today'),
      })
    },
    async setReportFilter(option) {
      if (!option.enabled) return
      if (option.key === this.reportFilter) return
      const previousStatus = this.activeStatusFilter
      this.reportFilter = option.key
      // V405-A: teacher sessions cannot be narrowed by course, so choosing
      // «با معلم برنا» returns the subject filter to «همه درس‌ها».
      if (option.key === 'teacher' && this.courseFilter !== 'all') {
        this.setCourseFilter('all')
        return
      }
      // UX2-03 (D-4): a status chip is a SERVER filter -- page 1 of the
      // catalog is re-read with `status=<bucket>`; nothing is filtered on the
      // client. Leaving a status chip for «همه» re-reads the unfiltered list.
      if (this.activeStatusFilter !== previousStatus) {
        await this.loadAiTutorConversations()
      }
    },
    // V405-A: a real filter -- the catalog is re-read with the API's own
    // `course` parameter; nothing is filtered or counted on the client.
    async setCourseFilter(course) {
      const next = course || 'all'
      if (next === this.courseFilter) return
      this.courseFilter = next
      if (next !== 'all') this.reportFilter = 'all'
      await this.loadAiTutorConversations()
    },
    teacherSessionMeta(session) {
      const parts = [
        this.$t('aiTutorSetup.reports.card.minutes', {
          count: this.formatCount(this.getSessionDuration(session.timeTutored)),
        }),
      ]
      if (session.volunteerFirstName) {
        parts.push(
          this.$t('aiTutorSetup.reports.card.teacherName', {
            name: session.volunteerFirstName,
          })
        )
      }
      return parts.join(' · ')
    },
    // CAT-1A: the cross-course conversation catalog + summary, two pure Borna
    // DB reads. A failure only marks THIS section as failed; the rest of
    // Reports is unaffected. The summary is optional: the list still renders
    // when only the summary read fails (the tiles then show `unavailable`).
    async loadAiTutorHistory() {
      this.aiTutorSummaryStatus = 'loading'
      await this.loadAiTutorConversations()
      // P42-06: only the newest summary read counts (a return-refresh may
      // have been asked for while this one was still running).
      this.aiTutorSummarySeq = (this.aiTutorSummarySeq || 0) + 1
      const summarySeq = this.aiTutorSummarySeq
      try {
        const summary = await aiTutorCollaborators.api.getReportsSummary()
        if (summarySeq !== this.aiTutorSummarySeq) return
        this.aiTutorSummary = summary
        this.aiTutorSummaryStatus = 'loaded'
      } catch (err) {
        if (summarySeq !== this.aiTutorSummarySeq) return
        this.aiTutorSummary = null
        this.aiTutorSummaryStatus = 'error'
      }
    },
    // P42-06 (owner decision 4): the data on screen can be older than what
    // Subway holds -- a tap recorded while this page was being read, or on
    // another tab. Three triggers re-read it, all through the existing
    // endpoints and all bounded: entering the page (`created`, as ever), the
    // confirmation of a tap in this app (store watcher), and coming back to
    // the page after the tab or window was left (`onAiTutorReturn`). It is a
    // SILENT read: the cards stay as they are (no dimming, no skeleton, no
    // error replacing them) and are replaced only by a newer answer. Filters
    // and the page are kept. A read already running is never doubled: a
    // confirmed tap queues ONE follow-up, a return is dropped. Separate
    // browser tabs are not coordinated
    // (no polling, no storage or broadcast channel): a tab that stays in view
    // while another records a tap updates when it is next returned to.
    async refreshAiTutorFreshness(reason) {
      if (!this.showAiTutorHistory || this._isDestroyed) return
      if (this.aiTutorStatus === 'idle') return
      if (this.aiTutorRefreshing || this.aiTutorReading) {
        // A confirmed tap must not be lost (one follow-up); a plain return
        // that overlaps a running read adds nothing.
        if (reason !== 'return') this.aiTutorRefreshQueued = true
        return
      }
      if (reason === 'return') {
        const now = Date.now()
        if (now - (this.aiTutorRefreshedAt || 0) < RETURN_REFRESH_MIN_GAP_MS)
          return
      }
      this.aiTutorRefreshedAt = Date.now()
      // A failed earlier read: bring it back the visible way (its retry).
      if (this.aiTutorStatus === 'error') {
        await this.loadAiTutorHistory()
        return
      }
      this.aiTutorRefreshing = true
      try {
        await Promise.all([
          this.loadAiTutorConversations(this.aiTutorPage, { silent: true }),
          this.refreshAiTutorSummary(),
        ])
      } finally {
        this.aiTutorRefreshing = false
        if (this.aiTutorRefreshQueued && !this.aiTutorReading) {
          this.aiTutorRefreshQueued = false
          this.refreshAiTutorFreshness('queued')
        }
      }
    },
    // The summary on its own, without the tiles going back to "loading"; a
    // failure keeps what is on screen.
    async refreshAiTutorSummary() {
      this.aiTutorSummarySeq = (this.aiTutorSummarySeq || 0) + 1
      const seq = this.aiTutorSummarySeq
      try {
        const summary = await aiTutorCollaborators.api.getReportsSummary()
        if (seq !== this.aiTutorSummarySeq || this._isDestroyed) return
        this.aiTutorSummary = summary
        this.aiTutorSummaryStatus = 'loaded'
      } catch (err) {
        // keep the summary that is on screen
      }
    },
    // The tab or window was left (hidden, or lost focus) ...
    onAiTutorAway() {
      if (!this.aiTutorAwaySince) this.aiTutorAwaySince = Date.now()
    },
    // ... and is back (visible, or focused, or restored from the back/forward
    // cache). One transition is one refresh: `visibilitychange` and `focus`
    // both fire for a tab switch, the second finds nothing left to answer.
    onAiTutorReturn() {
      if (!this.aiTutorAwaySince) return
      this.aiTutorAwaySince = 0
      this.refreshAiTutorFreshness('return')
    },
    onAiTutorVisibility() {
      if (typeof document === 'undefined') return
      if (document.visibilityState === 'hidden') this.onAiTutorAway()
      else this.onAiTutorReturn()
    },
    onAiTutorPageShow(event) {
      if (event && event.persisted) {
        this.aiTutorAwaySince = this.aiTutorAwaySince || Date.now()
        this.onAiTutorReturn()
      }
    },
    // The catalog read on its own, for the current subject filter. A late
    // answer for a filter the student has already left is dropped.
    // P41 QA-34: `page` of the server's list (a filter change reads page 1;
    // page 1 sends no `page`, exactly the call it always was). Only the
    // newest read counts: an answer overtaken by another read is dropped.
    async loadAiTutorConversations(page = 1, { silent = false } = {}) {
      const course = this.courseFilter
      // UX2-03: the status chip travels as the server's `status` bucket; its
      // `total` is the server's count for that bucket.
      const status = this.activeStatusFilter
      const query = { limit: CONVERSATIONS_PAGE_LIMIT }
      if (page > 1) query.page = page
      if (course !== 'all') query.course = course
      if (status) query.status = status
      this.aiTutorReadSeq = (this.aiTutorReadSeq || 0) + 1
      const seq = this.aiTutorReadSeq
      const stale = () =>
        seq !== this.aiTutorReadSeq ||
        course !== this.courseFilter ||
        status !== this.activeStatusFilter
      // P42-06: a silent read (the freshness refresh) leaves the status
      // alone: no dimming, no skeleton, and a failure leaves the cards.
      if (!silent) this.aiTutorStatus = 'loading'
      try {
        const result = await aiTutorCollaborators.api.getConversations(query)
        if (stale()) return
        const rows = Array.isArray(result && result.data) ? result.data : []
        const total =
          result && typeof result.total === 'number' ? result.total : null
        const limit =
          result && Number.isInteger(result.limit) && result.limit > 0
            ? result.limit
            : CONVERSATIONS_PAGE_LIMIT
        // The list shrank under a later page: show its last page instead.
        if (!rows.length && page > 1 && total) {
          const last = Math.max(1, Math.ceil(total / limit))
          if (last < page)
            return this.loadAiTutorConversations(last, { silent })
        }
        this.aiTutorConversations = rows
        this.aiTutorTotal = total
        this.aiTutorPageLimit = limit
        this.aiTutorPage = page
        if (course === 'all' && !status && page === 1) {
          const allRowsHere = total === null || total <= rows.length
          if (allRowsHere || !this.aiTutorCourses.length) {
            this.aiTutorCourses = this.coursesOf(rows)
          }
          if (!allRowsHere && !this.aiTutorCourseOptionsRead) {
            this.loadAiTutorCourseOptions()
          }
        }
        this.aiTutorStatus = 'loaded'
      } catch (err) {
        if (stale()) return
        if (silent) return
        // P41 QA-39: the rows on screen belong to an earlier read -- they are
        // not shown under the error as if they were this one's.
        this.aiTutorConversations = []
        this.aiTutorTotal = null
        this.aiTutorPage = 1
        this.aiTutorStatus = 'error'
      }
    },
    // P41 QA-34: the pager. The list is read again for that page; once it is
    // on screen the section's top is brought into view (the pager sits under
    // the last card).
    // The pressed pager button is disabled while the page loads, which drops
    // its focus; a keyboard user lands on the new page's first card instead
    // (review P3).
    async goToAiTutorPage(page) {
      if (this.aiTutorLoadKind) return
      if (page < 1 || page > this.aiTutorPageCount || page === this.aiTutorPage)
        return
      const pager =
        this.$el && this.$el.querySelector
          ? this.$el.querySelector('[data-testid="ai-tutor-history-pager"]')
          : null
      const pagerHadFocus = !!pager && pager.contains(document.activeElement)
      await this.loadAiTutorConversations(page)
      if (this.aiTutorStatus !== 'loaded' || this._isDestroyed) return
      const section =
        this.$el &&
        typeof this.$el.querySelector === 'function' &&
        this.$el.querySelector('[data-testid="ai-tutor-history"]')
      if (pagerHadFocus) {
        await this.$nextTick()
        const first = section && section.querySelector('.reports-card')
        if (first && typeof first.focus === 'function') {
          first.focus({ preventScroll: true })
        }
      }
      if (section && typeof section.scrollIntoView === 'function') {
        section.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }
    },
    // Batch 45: the catalog no longer fits on page 1, so the subject filter's
    // options get their own read, once per visit: the P42-06 freshness reads
    // stay one request each. Failing it leaves the options on screen.
    async loadAiTutorCourseOptions() {
      this.aiTutorCourseOptionsRead = true
      try {
        const result = await aiTutorCollaborators.api.getConversations({
          limit: COURSE_OPTIONS_LIMIT,
        })
        if (this._isDestroyed) return
        if (result && Array.isArray(result.data)) {
          this.aiTutorCourses = this.coursesOf(result.data)
        }
      } catch (err) {
        // the filter keeps the options it has; the next read may try again
        this.aiTutorCourseOptionsRead = false
      }
    },
    // The distinct courses of the catalog rows, in the order they first
    // appear (newest conversation first).
    coursesOf(conversations) {
      const seen = new Set()
      const courses = []
      conversations.forEach(conversation => {
        const name = conversation && conversation.courseName
        if (!name || seen.has(name)) return
        seen.add(name)
        courses.push({
          name,
          label: conversation.courseDisplayName || name,
        })
      })
      return courses
    },
    isActiveAiTutorConversation(conversation) {
      const active = this.aiTutorStore && this.aiTutorStore.activeSession
      return Boolean(
        active && conversation && active.id === conversation.synapseSessionId
      )
    },
    // P40 QA-33: only explicit `$…$` math is typeset in a card title.
    aiTutorTitleHasMath(conversation) {
      return /\$[^$]+\$/.test(this.aiTutorConversationTitle(conversation))
    },
    aiTutorConversationDate(conversation) {
      return this.formatReportTime(
        conversation.lastTurnAt || conversation.createdAt
      )
    },
    // Title source order (doc 20 §5): the learn topic snapshot, else the
    // tutor's current node (a Synapse cache), else the course. Never invented.
    aiTutorConversationTitle(conversation) {
      return (
        conversation.topic ||
        conversation.currentNodeTitle ||
        conversation.courseDisplayName ||
        this.$t('aiTutorSetup.sessions.untitled')
      )
    },
    // V2 meta line: mode · course · channel.
    aiTutorConversationMeta(conversation) {
      const parts = []
      if (conversation.intent && INTENT_LABEL_KEYS[conversation.intent]) {
        parts.push(this.$t(INTENT_LABEL_KEYS[conversation.intent]))
      }
      if (conversation.courseDisplayName) {
        parts.push(conversation.courseDisplayName)
      }
      parts.push(this.$t('aiTutorSetup.reports.card.channelAi'))
      return parts.join(' · ')
    },
    aiTutorConversationIcon(conversation) {
      return INTENT_ICONS[conversation.intent] || NEUTRAL_INTENT_ICON
    },
    // The V2 head pill, from OBSERVED state only. No learning-state verdict
    // («یاد گرفتی!» / «نیاز به تمرین») exists in the data (H-9), so the pill
    // names what Borna actually recorded: the open conversation, a pending
    // ask, a teacher handoff trigger, or the last feedback the student gave.
    aiTutorConversationStatus(conversation) {
      const t = key => this.$t(`aiTutorSetup.reports.status.${key}`)
      if (this.isActiveAiTutorConversation(conversation)) {
        return {
          kind: 'current',
          tone: 'indigo',
          icon: 'chat',
          label: t('current'),
        }
      }
      // UX2-03: the pill is the shared bucket (chip semantics == pill
      // semantics). Feedback buckets are stated only while Borna's ledger
      // backs `lastFeedbackKind`; a backfill row whose feedback was never
      // observed has no bucket and keeps the neutral pill.
      const bucket = conversationStatusBucket(conversation)
      if (bucket === 'awaiting') {
        return {
          kind: 'awaiting',
          tone: 'indigo',
          icon: 'dot',
          label: t('awaiting'),
        }
      }
      if (bucket === 'handoff') {
        return {
          kind: 'handoff',
          tone: 'amber',
          icon: 'teacher',
          label: t('handoff'),
        }
      }
      if (this.feedbackObservable) {
        if (bucket === 'understood') {
          return {
            kind: 'understood',
            tone: 'teal',
            icon: 'check',
            label: t('understood'),
          }
        }
        if (bucket === 'confused') {
          return {
            kind: 'confused',
            tone: 'amber',
            icon: 'spark',
            label: t('confused'),
          }
        }
        if (bucket === 'noFeedback') {
          return {
            kind: 'noFeedback',
            tone: 'grey',
            icon: 'book',
            label: t('noFeedback'),
          }
        }
      }
      return {
        kind: 'conversation',
        tone: 'grey',
        icon: 'chat',
        label: t('aiConversation'),
      }
    },
    // The V2 foot note, from observed facts: the message count (NULL = not
    // observed: nothing, never "0 messages") and the pending-ask / handoff
    // observation.
    aiTutorConversationNote(conversation) {
      const parts = []
      if (typeof conversation.turnCount === 'number') {
        parts.push(
          this.$t('aiTutorSetup.reports.turns', {
            count: this.formatCount(conversation.turnCount),
          })
        )
      }
      if (conversation.hasPendingAsk === true) {
        parts.push(this.$t('aiTutorSetup.reports.card.awaitingNote'))
      } else if (conversation.handoffTrigger) {
        parts.push(this.$t('aiTutorSetup.reports.referredToTeacher'))
      }
      return parts.join(' · ')
    },
    // Resume = `openConversation` (the catalog row supplies the course context,
    // then the EXISTING openSession flow: getSession + getTurns -> active),
    // then the AI Tutor chat, whose `activeSession.id` watcher opens the SSE.
    // The chat is only entered once the session really became active.
    async resumeAiTutorConversation(conversation) {
      if (this.aiTutorOpeningId) return
      // P41 QA-39: a card of the previous filter / page, while the new list
      // loads, is inert.
      if (this.aiTutorLoadKind === 'refresh') return
      const sessionId = conversation.synapseSessionId
      if (!sessionId) return
      this.aiTutorOpeningId = sessionId
      this.aiTutorOpenFailed = false
      try {
        const opened = await this.$store.dispatch('aiTutor/openConversation', {
          synapseSessionId: sessionId,
          courseName: conversation.courseName,
          intent: conversation.intent,
          grade: conversation.gradeName,
          topic: conversation.topic,
          stuckReason: conversation.stuckReason,
          proficiency: conversation.proficiency,
          // WB-M2A: lets the chat read the conversation's board-share
          // records, so a board picture keeps its «از تخته» badge.
          conversationId: conversation.id,
        })
        if (opened) {
          this.$router.push(`/${i18n.locale}/student/ai-tutor`)
        } else {
          // openSession leaves its failure in the store's `error`; it is
          // shown here, not later as a stale banner inside the chat.
          this.$store.commit('aiTutor/setError', null)
          this.aiTutorOpenFailed = true
        }
      } finally {
        this.aiTutorOpeningId = ''
      }
    },
    // WB-M1A: one entry per conversation that can have a board -- the
    // capability is available and the conversation is active with a session.
    aiTutorBoardEntries(conversation) {
      return this.whiteboardAvailable &&
        conversation &&
        conversation.status === 'active' &&
        conversation.synapseSessionId
        ? ['board']
        : []
    },
    // The Workspace opens the conversation itself (board first, chat
    // independently), so nothing is opened here.
    // P40 QA-28: offline, the Board's code may not load; the navigation then
    // fails and the network note says so instead of nothing happening.
    openAiTutorBoard(conversation) {
      // a card of the previous filter or page, still on screen while the new
      // list loads (review P3)
      if (this.aiTutorLoadKind === 'refresh') return
      this.aiTutorBoardOpenFailed = false
      Promise.resolve(
        this.$router.push(
          `/${i18n.locale}/student/ai-tutor/board/${encodeURIComponent(
            conversation.id
          )}`
        )
      ).catch(err => {
        if (isNavigationFailure(err) || this._isDestroyed) return
        this.aiTutorBoardOpenFailed = true
      })
    },
  },
  beforeDestroy() {
    if (typeof document === 'undefined') return
    document.removeEventListener('visibilitychange', this.onAiTutorVisibility)
    window.removeEventListener('blur', this.onAiTutorAway)
    window.removeEventListener('focus', this.onAiTutorReturn)
    window.removeEventListener('pageshow', this.onAiTutorPageShow)
  },
  async created() {
    if (!this.isVolunteer) {
      this.loadAiTutorHistory()
      // P42-06: coming back to this page refreshes it (see
      // `refreshAiTutorFreshness`).
      document.addEventListener('visibilitychange', this.onAiTutorVisibility)
      window.addEventListener('blur', this.onAiTutorAway)
      window.addEventListener('focus', this.onAiTutorReturn)
      window.addEventListener('pageshow', this.onAiTutorPageShow)
    }
    await Promise.all([
      this.getSessionHistory(this.page),
      this.getTotalSessions(),
    ])

    if (this.user.isVolunteer)
      AnalyticsService.captureEvent(EVENTS.VOLUNTEER_OPENED_SESSION_HISTORY)
  },
}
</script>

<style lang="scss" scoped>
a {
  text-decoration: none;
}

ul {
  padding: 0px;
  height: 100%;
  margin: auto;
  list-style-type: none;
}

.header {
  text-align: left;
  margin-bottom: 2em;

  @include breakpoint-below('small') {
    margin-bottom: 0;
  }
}

.title {
  font-weight: 500;
  font-size: 22px;
  margin-bottom: 1em;

  @include breakpoint-below('small') {
    margin: 1em;
    font-size: 18px;
  }

  &--body {
    text-align: center;
    margin: 3em 1em 0 1em;
  }
}

.subtitle {
  @include font-category('heading');
  color: $c-secondary-grey;
}

.session-history {
  padding: 53px;

  @include breakpoint-below('large') {
    padding: 1em;
  }

  @include breakpoint-below('small') {
    padding: 0;
  }

  &-loader {
    margin: 1em 0;
    @include flex-container(column, center, center);
  }
}

// Student Journey V2: the Student page gutters (see `_borna-tokens.scss`), for
// the acting STUDENT only -- the volunteer rules above are untouched. On
// desktop V2's Reports fills its column (`.app-content`'s 17px outer margin +
// 38px padding = 55px, measured at x=55); phones used to get NO gutter here
// while Home and the tutor setup kept 13-20px.
.session-history--student {
  padding: $borna-page-pad-top ($borna-page-pad-x + 17px) $borna-page-pad-bottom;

  @include borna-mobile {
    padding: $borna-page-pad-top-mobile $borna-page-pad-x-mobile
      $borna-page-pad-bottom-mobile;
  }

  @include borna-phone {
    padding-right: $borna-page-pad-x-phone;
    padding-left: $borna-page-pad-x-phone;
  }
}

.container {
  padding: 0;
  margin: 0;
  background-color: white;
  border: 1px solid $c-border-grey;
  border-radius: 8px 8px 16px 16px;
  min-width: 100%;
}

.spacing--grid {
  display: grid;
  text-align: center;

  &-3 {
    @include breakpoint-above('small') {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  &-4 {
    @include breakpoint-above('small') {
      grid-template-columns: 1fr 1fr 1fr 1fr;
    }
  }
}

.session-list {
  padding: 0 2em;
  min-height: 600px;

  @include breakpoint-below('large') {
    padding-right: 1.5em;
  }

  &__headers {
    @include font-category('subheading');
    background-color: $c-background-blue;
    width: 100%;
    padding: 1em 2em;
  }

  &__partner-name {
    @include font-category('subheading');
    margin: 0.8em;
    @include breakpoint-below('large') {
      font-size: 14px;
    }

    &-container {
      @include flex-container(row, center, center);

      @include breakpoint-below('medium') {
        width: 100px;
      }
    }
  }

  &__session {
    @include flex-container(row, space-around, center);
    display: grid;

    &--grid-3 {
      @include breakpoint-above('tiny') {
        grid-template-columns: 1fr 1fr 1fr;
      }
    }
    &--grid-4 {
      @include breakpoint-above('tiny') {
        grid-template-columns: 1fr 1fr 1fr 1fr;
      }
    }
    padding: 1em 0;
  }

  &__subject-container {
    @include flex-container(row, center, center);
  }

  &__created-at {
    text-align: center;
    @include font-category('subheading');
    color: $c-secondary-grey;
    @include breakpoint-below('large') {
      font-size: 14px;
      margin: 0.7em;
    }
  }

  &__session-recap {
    @include flex-container(row, center, center);

    &__button {
      border-color: $c-information-blue;
      background: #fff;
      color: $c-information-blue;
      fill: $c-information-blue;

      &:hover {
        background-color: rgba(24, 85, 209, 0.1);
        color: $c-information-blue;
        fill: $c-information-blue;
      }

      &:active {
        background-color: $c-information-blue;
        color: #fff;
        fill: #fff;
      }

      @include breakpoint-below('large') {
        transform: scale(0.8, 0.8);
      }
    }
  }
}

.subject {
  text-align: left;

  @include font-category('heading');
  @include breakpoint-below('large') {
    font-size: 16px;
  }

  &-icon {
    height: 50px;
    min-width: 50px;
    width: 50px;

    @include breakpoint-below('large') {
      min-width: initial;
      width: 30px;
    }
  }

  &-name-container {
    @include flex-container(column, center, flex-start);
    margin: 1.375em 0 1.375em 1em;
    width: 100px;

    @include breakpoint-below('large') {
      margin-left: 0.4em;
    }
  }

  &-time-tutored {
    @include font-category('helper-text');
    color: $c-secondary-grey;
    text-align: left;
  }
}

.border--thin {
  width: 100%;
  border-bottom: 2px solid $c-background-grey;
  margin: 0 auto;
}

.page-actions {
  @include flex-container(row, space-around);
  padding: 1em 0;

  @include breakpoint-above('large') {
    justify-content: flex-end;
  }
}

.page-actions-container {
  padding: 0 2em;
}

.page-numbers {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.page-actions {
  &__stepper {
    display: flex;
    align-items: center;

    margin-right: 1em;
    color: $c-information-blue;

    & .caret path {
      fill: $c-information-blue;
    }

    &:hover {
      cursor: pointer;
    }

    @include breakpoint-above('medium') {
      margin-right: 2em;
    }

    &--disabled {
      margin-right: 1em;
      color: $c-disabled-grey;

      &:hover {
        cursor: default;
      }

      & .caret path {
        fill: $c-disabled-grey;
      }

      @include breakpoint-above('medium') {
        margin-right: 2em;
      }
    }
  }
}

.page-num {
  margin-right: 1em;
  @include breakpoint-above('medium') {
    margin-right: 2em;
  }
  &:hover {
    color: $c-information-blue;
    cursor: pointer;
  }

  &--active {
    color: $c-information-blue;

    &:hover {
      cursor: default;
    }
  }
}

.caret {
  &--previous {
    transform: rotate(90deg);
    margin-right: 0.4em;
  }

  &--next {
    transform: rotate(-90deg);
    margin-left: 0.4em;
  }
}

// mobile css styling
.mobile-container {
  padding: 0.5em 1.5em 0.5em 1.5em;
  margin: 0;
  background-color: white;
  border: 1px solid $c-border-grey;
  border-radius: 8px 8px 16px 16px;
  min-width: 100%;
}

.mobile-session-list {
  padding: 0;
  min-height: 500px;
  &__partner-name {
    font-weight: 500;
    font-size: 14px;
    margin: 0.4em;
    &-container {
      @include flex-container(row, center, center);
    }
  }

  &__session {
    @include flex-container(row, space-between, center);
  }

  &__subject-container {
    @include flex-container(row, initial, center);
  }

  &__created-at {
    color: $c-secondary-grey;
    font-size: 12px;
    font-weight: 400;
  }

  &__createdAt-container {
    @include flex-container(column, center, flex-end);
    margin-left: auto;
  }
}

.mobile-subject {
  text-align: left;
  font-weight: 600;
  font-size: 16px;

  &-icon {
    height: 30px;
    width: 30px;
  }

  &-name-container {
    @include flex-container(column, center, flex-start);
    margin: 1.375em;
  }

  &-time-tutored {
    color: $c-secondary-grey;
    font-size: 12px;
    font-weight: 400;
  }
}

html.is-rtl {
  .header {
    text-align: right;
  }

  .session-list {
    @include breakpoint-below('large') {
      padding-left: 1.5em;
      padding-right: unset;
    }
  }

  .subject {
    text-align: right;

    &-name-container {
      margin: 1.375em 1em 1.375em 0;

      @include breakpoint-below('large') {
        margin-right: 0.4em;
        margin-left: unset;
      }
    }

    &-time-tutored {
      text-align: right;
    }
  }

  .page-actions {
    &__stepper {
      margin-left: 1em;
      margin-right: unset;

      @include breakpoint-above('medium') {
        margin-left: 2em;
        margin-right: unset;
      }

      &--disabled {
        margin-left: 1em;
        margin-right: unset;

        @include breakpoint-above('medium') {
          margin-left: 2em;
          margin-right: unset;
        }
      }
    }
  }

  .page-num {
    margin-left: 1em;
    margin-right: unset;
    @include breakpoint-above('medium') {
      margin-left: 2em;
      margin-right: unset;
    }
  }

  .caret {
    &--previous {
      transform: rotate(-90deg);
      margin-left: 0.4em;
      margin-right: unset;
    }

    &--next {
      transform: rotate(90deg);
      margin-right: 0.4em;
      margin-left: unset;
    }
  }

  .mobile-session-list {
    &__createdAt-container {
      margin-right: auto;
      margin-left: unset;
    }
  }

  .mobile-subject {
    text-align: right;
  }

  .learning-history {
    &__header,
    &__eyebrow,
    &__title,
    &__subtitle {
      text-align: right;
    }
  }

  .reports-card {
    &__title,
    &__meta,
    &__time,
    &__note {
      text-align: right;
    }
  }
}

// ---------------------------------------------------------------------------
// CAT-1C -- Student Reports V2 (DESIGN-18). Every value below was measured
// from the RENDERED canonical reference (`borna-student-journey-v2.html`,
// sha256 6ddb695a...3228) Reports screen at 1440 / 820 / 700 / 390, not read
// from its source: `.page-intro`, `.report-metrics`, `.learning-insight`,
// `.filter-row`, `.request-item` / `.report-card`. Colours the token layer
// does not carry (the amber request/practice tones, the coral intent tile, the
// grey pill) are the reference's own values, kept local to this surface.
// ---------------------------------------------------------------------------
$reports-amber: #b76e00; // rgb(183,110,0) -- request status, practice pill
$reports-amber-bg: #fff7e5; // rgb(255,247,229)
$reports-amber-border: #f2d59d; // rgb(242,213,157) -- the active request card
$reports-coral: #4668f0; // rgb(70,104,240) -- the learn intent tile, pulse dot
$reports-coral-bg: #eef3ff; // rgb(238,243,255)
$reports-grey-bg: #f1f5f9; // rgb(241,245,249) -- the neutral pill
$reports-insight-text: #42657a; // rgb(66,101,122)
$reports-disabled-bg: #f8fafc;
$reports-radius: 18px; // tiles, insight, cards
$reports-icon-radius: 13px; // 42px intent / metric tiles
$reports-icon-radius-phone: 11px; // 34px intent tile on the phone tier

.learning-history {
  // `.page-intro`: eyebrow 12/800, h1 27/400 (the base 1.75 line height),
  // lead 12/400 muted; 680px wide, 23px below.
  &__header {
    max-width: 680px;
    margin: 0 0 23px;
    text-align: left;
  }

  &__eyebrow {
    display: block;
    font-size: 12px;
    font-weight: $borna-fw-extrabold;
    // V2's eyebrow is an inline span inside the base 14px/1.75 line box.
    line-height: $borna-fs-base * $borna-lh-base;
    letter-spacing: 0.02em;
    color: $borna-indigo-600;
  }

  &__title {
    margin: 6px 0;
    font-size: 27px;
    font-weight: $borna-fw-regular;
    line-height: 1.75;
    color: $borna-ink;
  }

  &__subtitle {
    margin: 0;
    font-size: 12px;
    line-height: 1.75;
    color: $borna-muted;
  }

  &__state-icon {
    width: 40px;
    height: 40px;
    margin-bottom: 1em;
    opacity: 0.4;
  }
}

// V405-A (doc 26 §5) -- the rendered V405 reference's Reports toolbar
// (`BORNA_final405-07-06.html`, sha256 ea21cc7b...9cfd), measured at 1440 and
// 390: `.borna-reports-toolbar` (two columns, one from its 720px tier),
// `.borna-subject-filter`, `.borna-report-summary`. Everything else on this
// page measured identical to V2 and is unchanged.
.reports-toolbar {
  display: grid;
  grid-template-columns: minmax(210px, 0.72fr) minmax(0, 1.28fr);
  align-items: stretch;
  gap: 12px;
  padding: 14px 15px;
  margin: 0 0 14px;
  border: 1px solid $borna-border;
  border-radius: 20px;
  background: linear-gradient(135deg, #fff, #f8faff);
  box-shadow: 0 8px 24px rgba(7, 20, 71, 0.045);

  @include borna-compact {
    grid-template-columns: minmax(0, 1fr);
    gap: 9px;
    padding: 12px;
    border-radius: 17px;
  }

  &__filter {
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;

    label {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 0;
      font-size: 11px;
      font-weight: 850;
      line-height: 1.75;
      color: $borna-ink;

      ::v-deep svg {
        flex: none;
        color: $borna-indigo-600;
      }
    }

    select {
      box-sizing: border-box;
      width: 100%;
      height: 44px;
      margin: 0;
      padding: 0 11px;
      border: 1.5px solid $borna-border;
      border-radius: 12px;
      outline: 0;
      background-color: $borna-surface;
      font: inherit;
      font-size: 11px;
      font-weight: $borna-fw-bold;
      color: $borna-text;
      cursor: pointer;

      &:focus {
        border-color: $borna-indigo-600;
        box-shadow: 0 0 0 4px $borna-indigo-50;
      }

      @include borna-compact {
        height: 46px;
        font-size: 12px;
      }
    }
  }

  &__summary {
    display: flex;
    align-items: center;
    gap: 11px;
    min-width: 0;
    padding: 11px 13px;
    border: 1px solid $borna-indigo-100;
    border-radius: 14px;
    background: $borna-indigo-50;

    @include borna-compact {
      padding: 10px 11px;
    }
  }

  &__summary-icon {
    flex: none;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 11px;
    background: $borna-surface;
    color: $borna-indigo-600;
  }

  &__summary-copy {
    display: grid;
    gap: 2px;
    min-width: 0;

    strong {
      font-size: 11px;
      font-weight: $borna-fw-bold;
      line-height: 1.75;
      color: $borna-ink;
    }

    span {
      font-size: 10px;
      line-height: 1.75;
      color: $borna-muted;

      @include borna-compact {
        font-size: 9.5px;
      }
    }
  }
}

// `.report-metrics`: a 4-column grid, 11px gaps, 20px above / 14px below;
// 2 columns from the phone tier down. V2 never shows a tile narrower than
// 155px (its 700px mobile shell); in the accepted Student shell the sidebar
// is already open from 768px, which would squeeze four tiles to ~100px
// between 768 and 1023px, so that band keeps two columns as well.
.reports-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 11px;
  margin: 20px 0 14px;

  @media screen and (max-width: 1023.98px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @include borna-mobile {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @include borna-phone {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

// One metric `article`: 42px icon tile + value + label, 16px padding
// (12px on phones, with a 36px tile), 18px radius, 1px border.
.reports-tile {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  grid-template-rows: auto auto;
  align-items: center;
  column-gap: 10px;
  row-gap: 2px;
  padding: 16px;
  background: $borna-surface;
  border: 1px solid $borna-border;
  border-radius: $reports-radius;

  @include borna-phone {
    grid-template-columns: 36px minmax(0, 1fr);
    padding: 12px;
  }

  &__icon {
    grid-row: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: $reports-icon-radius;
    background: $borna-indigo-50;
    color: $borna-indigo-600;

    @include borna-phone {
      width: 36px;
      height: 36px;
    }
  }

  &__value {
    font-size: 19px;
    font-weight: $borna-fw-bold;
    line-height: 1.75;
    color: $borna-text;
  }

  &__label,
  &__note {
    font-size: 10px;
    line-height: 1.75;
    color: $borna-muted;
  }

  // The availability note shares the label line (V2's tile is one value row
  // and one label row: 86.8px at 1440); it wraps only where the tile is too
  // narrow for both, which is the reference's own behaviour for long labels.
  &__note::before {
    content: ' · ';
  }

  // In the mobile shell the tile column is too narrow for label and note on
  // one line (155-185px tiles at 700-820, 176px at 390): the note takes its
  // own line rather than breaking the label mid-phrase.
  @include borna-mobile {
    &__note {
      display: block;
    }

    &__note::before {
      content: none;
    }
  }

  // The approved unavailable presentations: the tile keeps its V2 place and
  // geometry, shows no number («—») and says why in the note. The icon tile
  // and the dash go muted so the state reads as "not a value" at a glance.
  &--unsupported,
  &--not_observable,
  &--unavailable,
  &--loading {
    .reports-tile__icon {
      background: $reports-grey-bg;
      color: $borna-muted-light;
    }

    .reports-tile__value {
      color: $borna-muted-light;
    }
  }

  &--loading .reports-tile__value {
    animation: reports-pulse 1.2s ease-in-out infinite;
  }
}

@keyframes reports-pulse {
  50% {
    opacity: 0.35;
  }
}

// `.learning-insight`: 16px padding, 18px radius, 24px below; a row on
// desktop, a column from the phone tier. Rendered ONLY in the approved
// unavailable state (no generated prose): the V2 teal becomes a neutral
// surface, the CTA is disabled.
.reports-insight {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  margin: 0 0 24px;
  border-radius: $reports-radius;
  background: $borna-teal-50;
  border: 1px solid $borna-teal-100;

  @include borna-phone {
    flex-direction: column;
    align-items: flex-start;
  }

  &__body {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
  }

  &__icon {
    flex: 0 0 auto;
    display: inline-flex;
    color: $borna-teal-700;
  }

  &__copy {
    min-width: 0;
  }

  &__title {
    display: block;
    font-size: 12px;
    font-weight: $borna-fw-bold;
    // Inline in V2, so it sits in the base 24.5px line box.
    line-height: $borna-fs-base * $borna-lh-base;
    color: $borna-text;
  }

  &__text {
    margin: 4px 0 0;
    font-size: 11px;
    line-height: 1.75;
    color: $reports-insight-text;
  }

  &__action {
    flex: 0 0 auto;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 11px;
    font-weight: $borna-fw-extrabold;
    line-height: 1.75;
    color: $borna-teal-700;
    cursor: pointer;
    align-self: flex-start;
  }

  &--unavailable {
    background: $reports-disabled-bg;
    border-color: $borna-border;

    .reports-insight__icon,
    .reports-insight__action {
      color: $borna-muted-light;
    }

    .reports-insight__text {
      color: $borna-muted;
    }

    .reports-insight__action {
      cursor: not-allowed;
    }
  }
}

// `.filter-row`: 7px gaps, 15px below; pills 11px, 7px 15px padding, 999px.
.reports-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: 0 0 15px;
}

.reports-filter {
  flex: 0 0 auto;
  padding: 7px 15px;
  border: 1px solid $borna-border;
  border-radius: $borna-radius-pill;
  background: $borna-surface;
  font: inherit;
  font-size: 11px;
  font-weight: $borna-fw-regular;
  line-height: 1.75;
  color: $borna-muted;
  cursor: pointer;

  &--active {
    font-weight: $borna-fw-extrabold;
    color: $borna-indigo-900;
    background: $borna-indigo-50;
    border-color: $borna-indigo-600;
  }

  // The approved disabled presentation for a filter without a data source:
  // same size, dashed border, muted copy, `disabled` + `aria-disabled` and an
  // off-screen explanation -- never a fake client-side filter. P42-10 moved
  // it to the end of the row and dropped the visible «غیرفعال» marker.
  // P40 QA-32: the pale copy on the near-page background read as a broken
  // pill, so the outline and copy are now legible, and the browser's own
  // disabled-button dimming (iOS fills disabled text separately) is neutral.
  &--disabled,
  &:disabled {
    border-style: dashed;
    border-color: $borna-muted-light;
    background: $reports-disabled-bg;
    color: $borna-muted;
    -webkit-text-fill-color: currentColor;
    opacity: 1;
    cursor: not-allowed;
  }
}

// `.report-list`: one column, 11px gaps. The two data sources keep their
// own group so each can load / fail / empty on its own.
.reports-list,
.reports-group {
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.reports-group:empty {
  display: none;
}

// `.request-item` / `.report-card`: white, 18px radius, 1px border, 17px
// padding; head (pill + time) / source (icon + copy) / foot (note + action).
.reports-card {
  display: block;
  padding: 17px;
  background: $borna-surface;
  border: 1px solid $borna-border;
  border-radius: $reports-radius;
  color: $borna-text;
  cursor: pointer;

  &:hover {
    box-shadow: $borna-shadow-card;
  }

  &:focus-visible {
    outline: 2px solid $borna-indigo-300;
    outline-offset: 2px;
  }

  // `.request-item--active`: the amber border and the 3px inset accent.
  &--request {
    border-color: $reports-amber-border;
    box-shadow: inset 3px 0 0 0 $reports-amber;

    &:hover {
      box-shadow: inset 3px 0 0 0 $reports-amber, $borna-shadow-card;
    }
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__time {
    flex: 0 0 auto;
    font-size: 8px;
    line-height: 1.75;
    color: $borna-muted;
    white-space: nowrap;
  }

  &__source {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 11px 0;
  }

  &__icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: $reports-icon-radius;

    @include borna-phone {
      width: 34px;
      height: 34px;
      border-radius: $reports-icon-radius-phone;
    }

    &--indigo {
      background: $borna-indigo-50;
      color: $borna-indigo-600;
    }

    &--teal {
      background: $borna-teal-50;
      color: $borna-teal-700;
    }

    &--coral {
      background: $reports-coral-bg;
      color: $reports-coral;
    }
  }

  &__copy {
    min-width: 0;
  }

  &__title {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: $borna-fw-regular;
    line-height: 1.75;
    color: $borna-text;
    overflow-wrap: anywhere;

    // The request card has no source row: its title follows the head.
    &--request {
      margin: 14px 0 5px;
    }
  }

  &__meta {
    margin: 0;
    font-size: 10px;
    line-height: 1.75;
    color: $borna-muted;
  }

  &__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 15px;
    padding-top: 12px;
    border-top: 1px solid $borna-border;
  }

  &__note {
    min-width: 0;
    font-size: 8px;
    line-height: 1.75;
    color: $borna-muted;
  }

  &__partner {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: default;
  }

  &__partner-name {
    font-size: 10px;
    font-weight: $borna-fw-medium;
    color: $borna-text;
  }

  &__action {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 8px;
    font-weight: $borna-fw-bold;
    line-height: 1.75;
    color: $borna-indigo-600;
    white-space: nowrap;
  }
}

// The head pill (`.status` / `.learning-status`): 10px, 6px 10px, 999px,
// 5px gap to its 14px glyph. Tones are the reference's; `warning` is its
// active-request pill (800 weight, 6px 9px, pulse dot).
.reports-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding: 6px 10px;
  border-radius: $borna-radius-pill;
  font-size: 10px;
  font-weight: $borna-fw-medium;
  line-height: 1.75;
  white-space: nowrap;
  background: $reports-grey-bg;
  color: $borna-muted;

  @include borna-phone {
    line-height: 1.6;
  }

  &--indigo {
    background: $borna-indigo-50;
    color: $borna-indigo-600;
  }

  &--teal {
    background: $borna-teal-50;
    color: $borna-teal-700;
  }

  &--amber {
    background: $reports-amber-bg;
    color: $reports-amber;
  }

  &--grey {
    background: $reports-grey-bg;
    color: $borna-muted;
  }

  &--warning {
    padding: 6px 9px;
    font-weight: $borna-fw-extrabold;
    background: $reports-amber-bg;
    color: $reports-amber;
  }

  &__dot {
    flex: 0 0 auto;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $reports-coral;
    box-shadow: 0 0 0 4px rgba(70, 104, 240, 0.13);
  }
}

// Loading / empty / error boxes share the card surface.
.reports-state {
  padding: 40px 24px;
  text-align: center;
  background: $borna-surface;
  border: 1px solid $borna-border;
  border-radius: $reports-radius;

  .title--body {
    margin: 0;
    font-size: 12px;
    font-weight: $borna-fw-regular;
    line-height: 1.75;
    color: $borna-muted;
  }

  &__retry {
    margin-top: 12px;
    padding: 7px 15px;
    border: 1px solid $borna-indigo-600;
    border-radius: $borna-radius-pill;
    background: $borna-indigo-50;
    font: inherit;
    font-size: 11px;
    font-weight: $borna-fw-extrabold;
    line-height: 1.75;
    color: $borna-indigo-900;
    cursor: pointer;
  }
}

.reports-note {
  margin: 0;
  font-size: 10px;
  line-height: 1.75;
  color: $borna-error-text;
}

// P41 QA-39: the first load of the AI Tutor list -- three cards in the
// approved report-card geometry (same padding, radius, border, head / source /
// foot rows), their text as grey bars that pulse like the summary tiles'
// loading value. No visual reference exists for a loading list; this reuses
// the card and the tiles' own loading treatment only.
.reports-skeletons {
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.reports-skeleton-card {
  display: block;
  padding: 17px;
  background: $borna-surface;
  border: 1px solid $borna-border;
  border-radius: $reports-radius;
}

.reports-skeleton {
  display: block;
  height: 10px;
  border-radius: $borna-radius-pill;
  background: $reports-grey-bg;
  animation: reports-pulse 1.2s ease-in-out infinite;

  &--pill {
    width: 84px;
    height: 20px;
  }

  &--time {
    width: 46px;
    height: 8px;
  }

  &--icon {
    width: 42px;
    height: 42px;
    border-radius: $reports-icon-radius;

    @include borna-phone {
      width: 34px;
      height: 34px;
      border-radius: $reports-icon-radius-phone;
    }
  }

  &--title {
    width: 62%;
    height: 12px;
    margin-bottom: 8px;
  }

  &--meta {
    width: 38%;
    height: 8px;
  }

  &--note {
    width: 30%;
    height: 8px;
  }

  &--action {
    width: 64px;
    height: 12px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
}

.reports-skeleton-card .reports-card__copy {
  flex: 1 1 auto;
}

.reports-skeleton-card .reports-card__foot {
  justify-content: space-between;
}

// P41 QA-39: a filter / page change -- the previous cards stay in place,
// dimmed and inert, under a thin running bar, until the new list arrives.
.reports-refreshing {
  position: relative;
  margin: 0;
  padding-top: 6px;
  font-size: 10px;
  line-height: 1.75;
  color: $borna-muted;

  &__bar {
    position: absolute;
    top: 0;
    inset-inline: 0;
    height: 3px;
    border-radius: $borna-radius-pill;
    background: $reports-grey-bg;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 30%;
      border-radius: inherit;
      background: $borna-indigo-300;
      animation: reports-refreshing 1.1s ease-in-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
      &::after {
        width: 100%;
        animation: none;
        opacity: 0.5;
      }
    }
  }
}

@keyframes reports-refreshing {
  from {
    inset-inline-start: -30%;
  }

  to {
    inset-inline-start: 100%;
  }
}

.reports-card--stale {
  opacity: 0.45;
  pointer-events: none;
  cursor: default;
}

.reports-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

// Continuation of the paginated teacher sessions: the same pill vocabulary
// as the filters (the reference shows no pager of its own).
.reports-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 4px 0 0;

  &__step {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 7px 15px;
    border: 1px solid $borna-border;
    border-radius: $borna-radius-pill;
    background: $borna-surface;
    font: inherit;
    font-size: 11px;
    font-weight: $borna-fw-bold;
    line-height: 1.75;
    color: $borna-indigo-600;
    cursor: pointer;

    &:disabled {
      color: $borna-muted-light;
      cursor: not-allowed;
    }
  }

  &__page {
    font-size: 11px;
    line-height: 1.75;
    color: $borna-muted;
  }
}

// WB-M1A: the board entry of an AI Tutor conversation card. Rendered only
// with Subway's whiteboard capability; it sits beside the card's action at
// the end of the foot. Functional styling, no visual reference. P42-12: an
// icon-only round button, 36 x 36 so a thumb can hit it.
.reports-board-entry {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-inline-start: auto;
  padding: 0;
  border: 1px solid $borna-indigo-100;
  border-radius: 999px;
  background: $borna-indigo-50;
  color: $borna-indigo-900;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $borna-indigo-600;
    outline-offset: 2px;
  }
}

// P43-02 (Owner UAT): the card's «continue where you left off» / recap action
// is a Forward arrow: «←» in Persian (as drawn), «→» in English. The pager's
// own previous / next arrows are not touched.
html:not(.is-rtl) .reports-card__action ::v-deep svg {
  transform: scaleX(-1);
}
</style>
